import { useCallback, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { formatUsd, formatDate, formatDay } from '../lib/format';
import { stripePromotionCodeUrl } from '../lib/external';

interface Coupon {
  stripe_promotion_code_id: string;
  code: string;
  name: string | null;
  percent_off: number | null;
  amount_off: number | null;
  max_redemptions: number | null;
  times_redeemed: number;
  expires_at: string | null;
  first_time_only: number;
  minimum_amount_cents: number | null;
  active: number;
  redeemable: boolean;
  order_count: number;
  discount_total: number;
}

interface CouponsResponse {
  stripe_mode: 'test' | 'live' | 'unset';
  last_synced_at: string | null;
  coupons: Coupon[];
}

const emptyForm = {
  code: '',
  name: '',
  type: 'percent' as 'percent' | 'amount',
  value: '',
  max_redemptions: '',
  expires_at: '',
  minimum: '',
  first_time_only: false,
};

const toCents = (dollars: string) => Math.round(parseFloat(dollars) * 100);

export function Coupons() {
  const [data, setData] = useState<CouponsResponse | null>(null);
  const [error, setError] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(
    () =>
      api
        .get<CouponsResponse>('/api/admin/coupons')
        .then(setData)
        .catch((e) => setError(e.message)),
    []
  );
  useEffect(() => {
    load();
  }, [load]);

  const sync = async () => {
    setSyncing(true);
    setError('');
    try {
      await api.post('/api/admin/coupons/sync');
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSyncing(false);
    }
  };

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy('create');
    setError('');
    try {
      await api.post('/api/admin/coupons', {
        code: form.code,
        name: form.name || undefined,
        type: form.type,
        percent_off: form.type === 'percent' ? Number(form.value) : undefined,
        amount_off: form.type === 'amount' ? toCents(form.value) : undefined,
        max_redemptions: form.max_redemptions ? parseInt(form.max_redemptions, 10) : undefined,
        // End of the chosen day, in the admin's time zone.
        expires_at: form.expires_at ? new Date(`${form.expires_at}T23:59:59`).toISOString() : undefined,
        minimum_amount_cents: form.minimum ? toCents(form.minimum) : undefined,
        first_time_only: form.first_time_only,
      });
      setForm(emptyForm);
      setCreating(false);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const toggle = async (c: Coupon) => {
    setBusy(c.stripe_promotion_code_id);
    setError('');
    try {
      await api.put(`/api/admin/coupons/${c.stripe_promotion_code_id}`, { active: !c.active });
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const mode = data?.stripe_mode === 'test' ? 'test' : 'live';

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16 }}>
        <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 28, fontWeight: 400 }}>
          Coupons
          {data?.stripe_mode === 'test' && <span style={modeBadge}>STRIPE TEST MODE</span>}
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 12, color: '#6b7280' }}>
            {data?.last_synced_at ? `Synced ${formatDate(data.last_synced_at)}` : 'Never synced'}
          </span>
          <button type="button" onClick={sync} disabled={syncing} style={btnSecondary}>
            {syncing ? 'Syncing…' : 'Sync from Stripe'}
          </button>
          <button type="button" onClick={() => setCreating((v) => !v)} style={btnPrimary}>
            {creating ? 'Cancel' : 'New coupon'}
          </button>
        </div>
      </div>
      <p style={{ fontSize: 13, color: '#6b7280', margin: '0 0 24px' }}>
        Codes live in Stripe and are entered by shoppers on the Stripe Checkout page. The promo
        field only appears there while at least one code is redeemable. Use Sync to pull in codes
        created in the Stripe dashboard and refresh redemption counts.
      </p>

      {error && (
        <div style={{ padding: 12, marginBottom: 16, background: '#fef2f2', color: '#b91c1c', borderRadius: 6, fontSize: 13 }}>
          {error}
        </div>
      )}

      {creating && (
        <form onSubmit={create} style={card}>
          <div style={formGrid}>
            <Field label="Code">
              <input
                required
                value={form.code}
                onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })}
                placeholder="SPRING20"
                maxLength={40}
                style={input}
              />
            </Field>
            <Field label="Name (shown on receipts)">
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Defaults to the code"
                maxLength={40}
                style={input}
              />
            </Field>
            <Field label="Discount">
              <div style={{ display: 'flex', gap: 6 }}>
                <select
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value as 'percent' | 'amount' })}
                  style={{ ...input, width: 90 }}
                >
                  <option value="percent">% off</option>
                  <option value="amount">$ off</option>
                </select>
                <input
                  required
                  type="number"
                  min={0.01}
                  max={form.type === 'percent' ? 100 : undefined}
                  step={0.01}
                  value={form.value}
                  onChange={(e) => setForm({ ...form, value: e.target.value })}
                  placeholder={form.type === 'percent' ? '20' : '10.00'}
                  style={{ ...input, flex: 1 }}
                />
              </div>
            </Field>
            <Field label="Max redemptions">
              <input
                type="number"
                min={1}
                step={1}
                value={form.max_redemptions}
                onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })}
                placeholder="Unlimited"
                style={input}
              />
            </Field>
            <Field label="Expires (end of day)">
              <input
                type="date"
                value={form.expires_at}
                onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                style={input}
              />
            </Field>
            <Field label="Minimum order ($, before shipping)">
              <input
                type="number"
                min={0.01}
                step={0.01}
                value={form.minimum}
                onChange={(e) => setForm({ ...form, minimum: e.target.value })}
                placeholder="None"
                style={input}
              />
            </Field>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, margin: '16px 0' }}>
            <input
              type="checkbox"
              checked={form.first_time_only}
              onChange={(e) => setForm({ ...form, first_time_only: e.target.checked })}
            />
            First-time customers only
          </label>
          <button type="submit" disabled={busy === 'create'} style={btnPrimary}>
            {busy === 'create' ? 'Creating in Stripe…' : 'Create coupon'}
          </button>
          <p style={{ fontSize: 11, color: '#9ca3af', margin: '8px 0 0' }}>
            Stripe doesn't allow editing a code's discount or limits later. To change one, deactivate it and
            create a new code.
          </p>
        </form>
      )}

      <div style={{ background: '#fff', border: '1px solid #e5e5e5', borderRadius: 8, overflow: 'hidden' }}>
        <div style={{ ...rowStyle, fontSize: 12, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 1 }}>
          <span>Code</span>
          <span>Discount</span>
          <span>Conditions</span>
          <span>Used</span>
          <span>Status</span>
          <span />
        </div>
        {data && data.coupons.length === 0 && (
          <p style={{ padding: 24, color: '#6b7280', margin: 0 }}>
            No coupons yet. Create one, or sync codes that already exist in Stripe.
          </p>
        )}
        {data?.coupons.map((c) => (
          <div key={c.stripe_promotion_code_id} style={{ ...rowStyle, opacity: c.redeemable ? 1 : 0.6 }}>
            <span>
              <a
                href={stripePromotionCodeUrl(c.stripe_promotion_code_id, mode)}
                target="_blank"
                rel="noreferrer"
                style={{ fontFamily: 'ui-monospace, monospace', fontSize: 14, fontWeight: 600, color: '#1a1a1a' }}
              >
                {c.code}
              </a>
              {c.name && c.name !== c.code && (
                <span style={{ display: 'block', fontSize: 12, color: '#6b7280' }}>{c.name}</span>
              )}
            </span>
            <span style={{ fontSize: 14 }}>
              {c.percent_off != null ? `${c.percent_off}% off` : `${formatUsd(c.amount_off)} off`}
            </span>
            <span style={{ fontSize: 12, color: '#6b7280' }}>{conditions(c) || '—'}</span>
            <span style={{ fontSize: 13 }}>
              {c.times_redeemed}
              {c.max_redemptions != null && ` / ${c.max_redemptions}`}
              {c.order_count > 0 && (
                <span style={{ display: 'block', fontSize: 11, color: '#6b7280' }}>
                  {formatUsd(c.discount_total)} off {c.order_count} order{c.order_count === 1 ? '' : 's'}
                </span>
              )}
            </span>
            <span style={{ fontSize: 13 }}>{status(c)}</span>
            <span style={{ textAlign: 'right' }}>
              <button
                type="button"
                onClick={() => toggle(c)}
                disabled={busy === c.stripe_promotion_code_id}
                style={btnSmall}
              >
                {c.active ? 'Deactivate' : 'Activate'}
              </button>
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function conditions(c: Coupon) {
  const parts = [];
  if (c.minimum_amount_cents) parts.push(`min ${formatUsd(c.minimum_amount_cents)}`);
  if (c.first_time_only) parts.push('first order only');
  if (c.expires_at) parts.push(`until ${formatDay(c.expires_at)}`);
  return parts.join(' · ');
}

function status(c: Coupon) {
  if (c.redeemable) return <span style={{ color: '#15803d' }}>Active</span>;
  if (!c.active) return <span style={{ color: '#6b7280' }}>Inactive</span>;
  if (c.max_redemptions != null && c.times_redeemed >= c.max_redemptions) {
    return <span style={{ color: '#b45309' }}>Used up</span>;
  }
  return <span style={{ color: '#b45309' }}>Expired</span>;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <span style={{ display: 'block', fontSize: 12, color: '#6b7280', marginBottom: 4 }}>{label}</span>
      {children}
    </label>
  );
}

const rowStyle: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1.2fr 100px 1.4fr 110px 80px 100px',
  gap: 16,
  padding: '14px 20px',
  borderBottom: '1px solid #f0f0f0',
  alignItems: 'center',
};

const card: React.CSSProperties = {
  background: '#fff',
  border: '1px solid #e5e5e5',
  borderRadius: 8,
  padding: 20,
  marginBottom: 24,
};

const formGrid: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
  gap: 16,
};

const input: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid #e5e5e5',
  borderRadius: 6,
  fontSize: 14,
  boxSizing: 'border-box',
};

const modeBadge: React.CSSProperties = {
  marginLeft: 12,
  padding: '2px 8px',
  borderRadius: 4,
  background: '#fef3c7',
  color: '#92400e',
  fontSize: 11,
  fontFamily: 'system-ui, sans-serif',
  fontWeight: 600,
  letterSpacing: 0.4,
  verticalAlign: 'middle',
};

const btnPrimary: React.CSSProperties = {
  padding: '10px 16px',
  background: '#1a1a1a',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontSize: 13,
  cursor: 'pointer',
};

const btnSecondary: React.CSSProperties = {
  padding: '9px 14px',
  background: '#fff',
  color: '#1a1a1a',
  border: '1px solid #d4d4d4',
  borderRadius: 6,
  fontSize: 13,
  cursor: 'pointer',
};

const btnSmall: React.CSSProperties = { ...btnSecondary, padding: '4px 10px', fontSize: 12 };
